/**
 * Public reviews section for `VenueDetailPage`.
 *
 * Composed from primitive blocks so the layout builder can show/hide each
 * one independently:
 *   - <RatingChip>            (small inline 4.6 ★ · 128)
 *   - <RatingScoreCard>       (big number + total)
 *   - <RatingDistribution>    (5★ … 1★ horizontal bars)
 *   - <ReviewSubmissionForm>  (logged-in user submits)
 *   - <ReviewList>            (approved reviews + owner replies)
 *
 * The parent VenueDetailPage decides which block to render via
 * `showSection(layoutKey, toggleKey)` — these components are stateless wrt
 * gating so they can be reused on cards, search results, etc.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Star, MessageSquare, Loader2, Pencil } from "lucide-react";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";


/* =============== RATING CHIP (cards/header inline) =============== */

export function RatingChip({ summary, compact = false, className = "", testId }) {
  const { t } = useTranslation();
  if (!summary || !summary.total) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 ${compact ? "text-sm" : "text-base"} ${className}`}
      data-testid={testId || "rating-chip"}
    >
      <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
      <span className="font-medium tabular-nums">{Number(summary.average).toFixed(1)}</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground tabular-nums">
        {compact ? summary.total : t("reviews:header_count", { count: summary.total })}
      </span>
    </span>
  );
}


/* =============== BIG SCORE CARD =============== */

export function RatingScoreCard({ summary }) {
  const { t } = useTranslation();
  if (!summary) return null;
  if (!summary.total) {
    return (
      <div className="bg-muted/30 rounded-xl p-6 text-center" data-testid="rating-score-card">
        <p className="text-muted-foreground">{t("reviews:header_count_zero")}</p>
      </div>
    );
  }
  return (
    <div className="bg-muted/30 rounded-xl p-6 flex items-center gap-6" data-testid="rating-score-card">
      <div className="text-center">
        <p className="text-5xl font-medium text-foreground tabular-nums">{Number(summary.average).toFixed(1)}</p>
        <div className="flex items-center justify-center gap-0.5 mt-1">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className={`h-4 w-4 ${s <= Math.round(summary.average) ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground/40"}`} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1">{t("reviews:header_count", { count: summary.total })}</p>
      </div>
    </div>
  );
}


/* =============== DISTRIBUTION BARS =============== */

export function RatingDistribution({ summary }) {
  const { t } = useTranslation();
  if (!summary || !summary.total) return null;
  const total = summary.total || 1;
  return (
    <div className="space-y-1.5" data-testid="rating-distribution">
      <p className="text-xs font-medium text-muted-foreground mb-2">{t("reviews:rating_distribution")}</p>
      {[5, 4, 3, 2, 1].map((s) => {
        const count = summary.distribution?.[s] || 0;
        const pct = (count / total) * 100;
        return (
          <div key={s} className="flex items-center gap-3 text-sm" data-testid={`distribution-row-${s}`}>
            <span className="w-6 tabular-nums text-muted-foreground">{s}★</span>
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-yellow-500 transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-10 text-right tabular-nums text-xs text-muted-foreground">{count}</span>
          </div>
        );
      })}
    </div>
  );
}


/* =============== SUBMISSION FORM =============== */

export function ReviewSubmissionForm({ venueId, onSubmitted }) {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [predefinedKey, setPredefinedKey] = useState("");
  const [emailVisible, setEmailVisible] = useState(false);
  const [language, setLanguage] = useState(i18n.language || "en");
  const [predefinedOpts, setPredefinedOpts] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Fetch predefined texts whenever the rating changes — no extra round-trip
  // when no rating is selected yet (avoid hammering the public API).
  useEffect(() => {
    if (!rating) { setPredefinedOpts([]); return; }
    axios
      .get(`${API}/predefined-review-texts`, { params: { rating, lang: language } })
      .then(({ data }) => setPredefinedOpts(data || []))
      .catch(() => setPredefinedOpts([]));
  }, [rating, language]);

  if (!isAuthenticated) {
    return (
      <div className="bg-muted/30 rounded-xl p-6 text-center" data-testid="review-submission-login-prompt">
        <p className="text-muted-foreground">{t("reviews:login_required")}</p>
      </div>
    );
  }

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!rating) {
      toast.error(t("reviews:rating_required"));
      return;
    }
    if (!text.trim() && !predefinedKey) {
      toast.error(t("reviews:rating_required"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reviews`, {
        venue_id: venueId,
        rating,
        review_text: text.trim() || "",
        predefined_text_key: predefinedKey || null,
        language,
        email_visible_to_owner: emailVisible,
      });
      // Backend returns the formatted review; whether it's pending or approved
      // depends on settings. Show the right success copy either way.
      const isLive = data?.status === "approved";
      toast.success(isLive
        ? t("reviews:submit_success_no_mod")
        : t("reviews:submit_success"));
      setRating(0); setText(""); setPredefinedKey(""); setHover(0);
      onSubmitted?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-card rounded-xl border border-border p-6 space-y-4" data-testid="review-submission-form">
      <h3 className="text-lg font-medium text-foreground">{t("reviews:leave_review")}</h3>

      <div>
        <Label className="text-sm">{t("reviews:rating_label")}</Label>
        <div className="flex items-center gap-1 mt-2" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => setRating(s)}
              onMouseEnter={() => setHover(s)}
              className="p-1 transition-transform hover:scale-110"
              data-testid={`review-star-${s}`}
              aria-label={t(`reviews:stars_${s}`)}
            >
              <Star className={`h-7 w-7 ${s <= (hover || rating) ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground/40"}`} />
            </button>
          ))}
          {rating > 0 && <span className="ml-2 text-sm text-muted-foreground">{t(`reviews:stars_${rating}`)}</span>}
        </div>
      </div>

      <div>
        <Label className="text-sm">{t("reviews:review_text_label")}</Label>
        <Textarea
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("reviews:review_text_placeholder")}
          className="mt-1"
          data-testid="review-text"
          maxLength={1000}
        />
      </div>

      {predefinedOpts.length > 0 && (
        <div>
          <Label className="text-sm">{t("reviews:predefined_label")}</Label>
          <Select value={predefinedKey || "__none__"} onValueChange={(v) => setPredefinedKey(v === "__none__" ? "" : v)}>
            <SelectTrigger className="mt-1" data-testid="review-predefined">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t("reviews:predefined_none")}</SelectItem>
              {predefinedOpts.map((p) => (
                <SelectItem key={p.predefined_id} value={p.predefined_id}>
                  {p.text || (language === "el" ? p.text_el : p.text_en)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm">{t("reviews:language_label")}</Label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="mt-1" data-testid="review-lang"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="el">Ελληνικά</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="flex items-center justify-between gap-3 mt-6">
            <div className="text-sm">
              <p className="text-foreground">{t("reviews:email_visible_label")}</p>
              <p className="text-xs text-muted-foreground">
                {emailVisible ? t("reviews:email_hint_visible") : t("reviews:email_hint_hidden")}
              </p>
            </div>
            <Switch checked={emailVisible} onCheckedChange={setEmailVisible} data-testid="review-email-visible" />
          </label>
        </div>
      </div>

      <Button type="submit" disabled={submitting || !rating} data-testid="review-submit">
        {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Star className="h-4 w-4 mr-2" />}
        {submitting ? t("reviews:submitting") : t("reviews:submit")}
      </Button>
    </form>
  );
}


/* =============== REVIEW LIST + OWNER REPLY =============== */

export function ReviewList({ reviews, showOwnerReplies = true, currentLang = "en", onReviewUpdated }) {
  const { t } = useTranslation();
  if (!reviews || reviews.length === 0) {
    return <p className="text-muted-foreground" data-testid="reviews-empty">{t("reviews:no_reviews_yet")}</p>;
  }
  return (
    <div className="space-y-6" data-testid="reviews-list">
      {reviews.map((r) => (
        <ReviewCard
          key={r.review_id}
          review={r}
          showOwnerReply={showOwnerReplies}
          currentLang={currentLang}
          onUpdated={onReviewUpdated}
        />
      ))}
    </div>
  );
}


function ReviewCard({ review, showOwnerReply, currentLang, onUpdated }) {
  const { t } = useTranslation();
  const [showTranslation, setShowTranslation] = useState(false);
  const [editing, setEditing] = useState(false);
  const original = (review.review_text || "").trim()
    || (review.language === "el" ? review.predefined_text_el : review.predefined_text_en)
    || review.predefined_text_en
    || review.predefined_text_el
    || "";
  const translation = currentLang === "en" ? review.translated_en : review.translated_el;
  const showToggle = translation && translation !== original && review.language && review.language !== currentLang;
  const display = (showToggle && showTranslation) ? translation : original;

  // Edit-window math: deadline > now AND row is the requester's own.
  const deadline = review.edit_deadline_at ? new Date(review.edit_deadline_at) : null;
  const hoursLeft = deadline ? Math.max(0, Math.round((deadline.getTime() - Date.now()) / 3600000)) : 0;
  const canEdit = !!review.is_own_review && deadline && deadline > new Date();

  if (editing) {
    return <InlineEditForm review={review} onCancel={() => setEditing(false)} onSaved={(updated) => { setEditing(false); onUpdated?.(updated); }} />;
  }

  return (
    <div className="border-b border-border pb-6 last:border-0" data-testid={`public-review-${review.review_id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium">
          {review.user_name || t("reviews:anonymous")}
          {review.is_own_review && (
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
              {t("reviews:your_review", { defaultValue: "Your review" })}
            </span>
          )}
        </span>
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((s) => (
            <Star key={s} className={`h-4 w-4 ${s <= (review.rating || 0) ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground/40"}`} />
          ))}
        </div>
      </div>
      {display && <p className="text-muted-foreground whitespace-pre-wrap">{display}</p>}
      {showToggle && (
        <button
          type="button"
          onClick={() => setShowTranslation((v) => !v)}
          className="text-xs text-primary hover:underline mt-1"
          data-testid={`translate-toggle-${review.review_id}`}
        >
          {showTranslation ? t("reviews:hide_translation") : t("reviews:show_translation")}
        </button>
      )}
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-muted-foreground">
          {review.submitted_at?.slice(0, 10)}
          {review.edited_at && (
            <span className="ml-2 italic">· {t("reviews:edited", { defaultValue: "edited" })}</span>
          )}
        </p>
        {canEdit && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs text-primary hover:underline flex items-center gap-1"
            data-testid={`edit-review-${review.review_id}`}
          >
            <Pencil className="h-3 w-3" />
            {t("reviews:edit", { defaultValue: "Edit review" })}
            <span className="text-muted-foreground">
              · {t("reviews:edit_window_open", { hours: hoursLeft })}
            </span>
          </button>
        )}
        {review.is_own_review && !canEdit && (
          <span className="text-xs text-muted-foreground italic">
            {t("reviews:edit_window_closed")}
          </span>
        )}
      </div>
      {showOwnerReply && review.owner_reply_text && (
        <div className="mt-3 pl-4 border-l-2 border-primary bg-primary/5 rounded-r-lg p-3" data-testid={`owner-reply-${review.review_id}`}>
          <p className="text-xs font-medium text-primary mb-1 flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> {t("reviews:owner_reply_title")}
          </p>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap">{review.owner_reply_text}</p>
        </div>
      )}
    </div>
  );
}


/**
 * Inline review editor — shown when the user clicks "Edit review" on their
 * own review and the edit window is still open. Saves through PUT
 * `/reviews/{id}` which (when admin moderation is on) re-queues the review
 * as `pending` per spec.
 */
function InlineEditForm({ review, onCancel, onSaved }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(review.rating || 0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState(review.review_text || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error(t("reviews:rating_required"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/reviews/${review.review_id}`, {
        rating,
        review_text: text.trim(),
      });
      toast.success(t("reviews:edit_saved", { defaultValue: "Review updated" }));
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="border border-primary/40 bg-primary/5 rounded-xl p-4 space-y-3" data-testid={`edit-review-form-${review.review_id}`}>
      <p className="text-sm font-medium text-foreground">{t("reviews:edit")}</p>
      <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((s) => (
          <button
            type="button"
            key={s}
            onClick={() => setRating(s)}
            onMouseEnter={() => setHover(s)}
            className="p-1"
            data-testid={`edit-star-${review.review_id}-${s}`}
          >
            <Star className={`h-6 w-6 ${s <= (hover || rating) ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground/40"}`} />
          </button>
        ))}
      </div>
      <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("reviews:review_text_placeholder")} maxLength={1000} data-testid={`edit-text-${review.review_id}`} />
      <div className="flex gap-2">
        <Button size="sm" type="submit" disabled={saving} data-testid={`edit-save-${review.review_id}`}>
          {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Pencil className="h-4 w-4 mr-1" />}
          {t("reviews:save_changes")}
        </Button>
        <Button size="sm" type="button" variant="outline" onClick={onCancel} data-testid={`edit-cancel-${review.review_id}`}>
          {t("reviews:cancel")}
        </Button>
      </div>
    </form>
  );
}
